/*
 * QuestionnairePage Messages
 *
 * This contains all the text for the QuestionnairePage container.
 */
import { defineMessages } from 'react-intl';

import { ActionVisibilityValueType } from 'utils/adl-services';
import { ANSWER_OPTIONS } from 'containers/QuestionnairePage/NodeSettings/settings/utils/constants';
import {
  RATING_VALUES,
  RATING_LABELS,
} from 'containers/QuestionnairePage/GenericQuestion/RatingQuestion/constants';
import {
  MATRIX_MAX_ROWS,
  MATRIX_MAX_COLUMNS,
} from 'containers/QuestionnairePage/GenericQuestion/MatrixQuestion/constants';
import {
  QUOTA_BASED_ON,
  QUOTA_FILL_ORDER,
} from 'utils/quotaAssignmentStrategy';
import { QUOTA_MODE } from 'data/quotaModes';
import { CONFIRM_MODAL_TYPES } from './constants';

export const scope = 'app.containers.QuestionnairePage';

const messages = defineMessages({
  conversationalQuestion: {
    id: `${scope}.conversationalQuestion`,
    defaultMessage: 'Conversational AI',
  },
  noQuestions: {
    id: `${scope}.noQuestions`,
    defaultMessage: 'You have not added any questions',
  },
  maxQuestionnairePath: {
    id: `${scope}.maxQuestionnairePath`,
    defaultMessage: 'Questionnaire path length',
  },
  maxQuestionnairePathTooltip: {
    id: `${scope}.maxQuestionnairePathTooltip`,
    defaultMessage:
      'The Max Questionnaire path length is the maximum number of questions a respondent can answer when skip logic is enabled. Respondents are allowed to answer up to {questionLimit} questions per survey.',
  },
  singleSelection: {
    id: `${scope}.singleSelection`,
    defaultMessage: 'Single selection',
  },
  multipleSelection: {
    id: `${scope}.multipleSelection`,
    defaultMessage: 'Multiple selection',
  },
  openEnded: {
    id: `${scope}.openEnded`,
    defaultMessage: 'Open-ended',
  },
  openEndedNumeric: {
    id: `${scope}.openEndedNumeric`,
    defaultMessage: 'Numeric open-ended',
  },
  openEndedMatrix: {
    id: `${scope}.openEndedMatrix`,
    defaultMessage: 'Multiple open-ended',
  },
  descriptionQuestion: {
    id: `${scope}.descriptionQuestion`,
    defaultMessage: 'Description',
  },
  ratingQuestion: {
    id: `${scope}.ratingQuestion`,
    defaultMessage: 'Rating stars',
  },
  sliderQuestion: {
    id: `${scope}.sliderQuestion`,
    defaultMessage: 'Slider',
  },
  rankingQuestion: {
    id: `${scope}.rankingQuestion`,
    defaultMessage: 'Ranking',
  },
  matrixQuestion: {
    id: `${scope}.matrixQuestion`,
    defaultMessage: 'Matrix table',
  },
  constantSum: {
    id: `${scope}.constantSum`,
    defaultMessage: 'Constant sum',
  },
  matrixLimitInfo: {
    id: `${scope}.matrixLimitInfo`,
    defaultMessage: `You can select the minimum of 2 statements and 2 scale points - maximum of ${MATRIX_MAX_ROWS} statements and ${MATRIX_MAX_COLUMNS} scale points`,
  },
  matrixRowsLabel: {
    id: `${scope}.matrixRowsLabel`,
    defaultMessage: 'Statements',
  },
  matrixColumnsLabel: {
    id: `${scope}.matrixColumnsLabel`,
    defaultMessage: 'Scale points',
  },
  npsLabel: {
    id: `${scope}.npsLabel`,
    defaultMessage: 'Net Promoter Score<sup>&reg;</sup> (NPS)',
  },
  drilldownLabel: {
    id: `${scope}.drilldownLabel`,
    defaultMessage: 'Drill down',
  },
  groupLabel: {
    id: `${scope}.groupLabel`,
    defaultMessage: 'Group of Questions',
  },
  groupLoopAddMergeLabel: {
    id: `${scope}.groupLoopAddMergeLabel`,
    defaultMessage: 'Loop & Merge',
  },
  abLabel: {
    id: `${scope}.abLabel`,
    defaultMessage: 'A/B Test',
  },
  conjointLabel: {
    id: `${scope}.conjointLabel`,
    defaultMessage: 'Conjoint',
  },
  vanWestendorpLabel: {
    id: `${scope}.vanWestendorpLabel`,
    defaultMessage: 'Pricing study (Van Westendorp)',
  },
  vanWestendorpLabelShort: {
    id: `${scope}.vanWestendorpLabelShort`,
    defaultMessage: 'Pricing study (Van West.)',
  },
  maxDiffLabel: {
    id: `${scope}.maxDiffLabel`,
    defaultMessage: 'Maxdiff',
  },
  shuffleAnswers: {
    id: `${scope}.shuffleAnswers`,
    defaultMessage: 'Shuffle answers',
  },
  shuffleAnswersTooltip: {
    id: `${scope}.shuffleAnswersTooltip`,
    defaultMessage:
      '<b>Reverse options</b> flips the order of items, with a 50% chance.<br/><br/><b>Keep first/last fixed</b> protects those positions while the rest shuffle.',
  },
  batch: {
    id: `${scope}.batch`,
    defaultMessage: 'Batch',
  },
  batchAdd: {
    id: `${scope}.batchAdd`,
    defaultMessage: 'Save',
  },
  batchAnswers: {
    id: `${scope}.batchAnswers`,
    defaultMessage: 'Batch answers',
  },
  batchAnswersAdd: {
    id: `${scope}.batchAnswersAdd`,
    defaultMessage: 'Save answers',
  },
  batchAnswersPlaceholder: {
    id: `${scope}.batchAnswersPlaceholder`,
    defaultMessage: 'Enter each answer on a separate line',
  },
  delimiterPlaceholder: {
    id: `${scope}.delimiterPlaceholder`,
    defaultMessage:
      '(separate the left and the right part of a statement with the "|" character)',
  },
  cancel: {
    id: `${scope}.cancel`,
    defaultMessage: 'Cancel',
  },
  advancedLogic: {
    id: `${scope}.advancedLogic`,
    defaultMessage: 'Apply advanced logic',
  },
  applyDisplayLogic: {
    id: `${scope}.applyDisplayLogic`,
    defaultMessage: 'Apply display logic',
  },
  addMedia: {
    id: `${scope}.addMedia`,
    defaultMessage: 'Add media to question',
  },
  limitPriceRanges: {
    id: `${scope}.limitPriceRanges`,
    defaultMessage: 'Limit price range',
  },
  limitPriceRangesMin: {
    id: `${scope}.limitPriceRangesMin`,
    defaultMessage: 'Min',
  },
  limitPriceRangesMax: {
    id: `${scope}.limitPriceRangesMax`,
    defaultMessage: 'Max',
  },
  limitPriceRangesTooltip: {
    id: `${scope}.limitPriceRangesTooltip`,
    defaultMessage:
      'Set the range (min, max or both) of applicable prices for validation of responses. Try to keep it as wide as possible.',
  },
  vanWestendorpLikelihoodTooltip: {
    id: `${scope}.vanWestendorpLikelihoodTooltip`,
    defaultMessage:
      'Add Newton-Miller-Smith extension. Learn about the purchase intent at the price in which a respondent noted.',
  },
  allowDecimalNumbersLabel: {
    id: `${scope}.allowDecimalNumbersLabel`,
    defaultMessage: 'Prices with decimal points',
  },
  decimalCount: {
    id: `${scope}.decimalCount`,
    defaultMessage: 'Decimal points',
  },
  includeLikelihoodLabel: {
    id: `${scope}.includeLikelihoodLabel`,
    defaultMessage: 'Likelihood to purchase questions',
  },
  vanWestendorpLikelihoodLabel: {
    id: `${scope}.vanWestendorpLikelihoodLabel`,
    defaultMessage: 'N.M.S. extension',
  },
  questionOrderLabel: {
    id: `${scope}.questionOrderLabel`,
    defaultMessage: "Questions' order",
  },
  pricingQuestionsCountLabel: {
    id: `${scope}.pricingQuestionsCountLabel`,
    defaultMessage: 'Pricing questions',
  },
  addLimits: {
    id: `${scope}.addLimits`,
    defaultMessage: 'Limit answers',
  },
  addDecimalLimit: {
    id: `${scope}.addDecimalLimit`,
    defaultMessage: 'Allow decimal numbers',
  },
  maxDecimalLimit: {
    id: `${scope}.maxDecimalLimit`,
    defaultMessage: 'Maximum Decimals',
  },
  addLimitsRange: {
    id: `${scope}.addLimitsRange`,
    defaultMessage: 'Limit number range',
  },
  addLimitsRangeMin: {
    id: `${scope}.addLimitsRangeMin`,
    defaultMessage: 'Min',
  },
  addLimitsRangeMax: {
    id: `${scope}.addLimitsRangeMax`,
    defaultMessage: 'Max',
  },
  addLimitsInfo: {
    id: `${scope}.addLimitsInfo`,
    defaultMessage:
      'Setting both values to the same number will ensure an exact number of answers',
  },
  addLimitsMin: {
    id: `${scope}.addLimitsMin`,
    defaultMessage: 'At least',
  },
  addLimitsMax: {
    id: `${scope}.addLimitsMax`,
    defaultMessage: 'No more than',
  },
  ratingLabel: {
    id: `${scope}.ratingLabel`,
    defaultMessage: 'Number of stars',
  },
  rankingCustomLabel: {
    id: `${scope}.rankingCustomLabel`,
    defaultMessage: 'Custom Labels',
  },
  [RATING_LABELS.TWO]: {
    id: `${scope}.${RATING_LABELS.TWO}`,
    defaultMessage: `${RATING_VALUES.TWO} stars`,
  },
  [RATING_LABELS.THREE]: {
    id: `${scope}.${RATING_LABELS.THREE}`,
    defaultMessage: `${RATING_VALUES.THREE} stars`,
  },
  [RATING_LABELS.FOUR]: {
    id: `${scope}.${RATING_LABELS.FOUR}`,
    defaultMessage: `${RATING_VALUES.FOUR} stars`,
  },
  [RATING_LABELS.FIVE]: {
    id: `${scope}.${RATING_LABELS.FIVE}`,
    defaultMessage: `${RATING_VALUES.FIVE} stars`,
  },
  [RATING_LABELS.SIX]: {
    id: `${scope}.${RATING_LABELS.SIX}`,
    defaultMessage: `${RATING_VALUES.SIX} stars`,
  },
  [RATING_LABELS.SEVEN]: {
    id: `${scope}.${RATING_LABELS.SEVEN}`,
    defaultMessage: `${RATING_VALUES.SEVEN} stars`,
  },
  [RATING_LABELS.EIGHT]: {
    id: `${scope}.${RATING_LABELS.EIGHT}`,
    defaultMessage: `${RATING_VALUES.EIGHT} stars`,
  },
  [RATING_LABELS.NINE]: {
    id: `${scope}.${RATING_LABELS.NINE}`,
    defaultMessage: `${RATING_VALUES.NINE} stars`,
  },
  [RATING_LABELS.TEN]: {
    id: `${scope}.${RATING_LABELS.TEN}`,
    defaultMessage: `${RATING_VALUES.TEN} stars`,
  },
  [RATING_LABELS.ELEVEN]: {
    id: `${scope}.${RATING_LABELS.ELEVEN}`,
    defaultMessage: `${RATING_VALUES.ELEVEN} stars`,
  },
  predefinedAnswers: {
    id: `${scope}.predefinedAnswers`,
    defaultMessage: 'Predefined answers',
  },
  noneOfTheAbove: {
    id: `${scope}.noneOfTheAbove`,
    defaultMessage: 'Add "None of the above"',
  },
  noneOfTheAbovePlaceholder: {
    id: `${scope}.noneOfTheAbovePlaceholder`,
    defaultMessage: 'None of the above',
  },
  noneOfTheAboveLabel: {
    id: `${scope}.noneOfTheAboveLabel`,
    defaultMessage: 'Answer text',
  },
  noneOfTheAboveAlias: {
    id: `${scope}.noneOfTheAboveAlias`,
    defaultMessage: 'NON',
  },
  noneOfTheseLabel: {
    id: `${scope}.noneOfTheseLabel`,
    defaultMessage: 'Add "None of these"',
  },
  noneOfThesePlaceholder: {
    id: `${scope}.noneOfThesePlaceholder`,
    defaultMessage: 'None of these',
  },
  noneOfTheseAlias: {
    id: `${scope}.noneOfTheseAlias`,
    defaultMessage: 'NON',
  },
  addOther: {
    id: `${scope}.addOther`,
    defaultMessage: 'Add "Other"',
  },
  addOtherPlaceholder: {
    id: `${scope}.addOtherPlaceholder`,
    defaultMessage: 'Other',
  },
  addOtherPrompt: {
    id: `${scope}.addOtherPrompt`,
    defaultMessage: 'Please specify',
  },
  addOtherLabel: {
    id: `${scope}.addOtherLabel`,
    defaultMessage: 'Answer text',
  },
  addOtherPromptLabel: {
    id: `${scope}.addOtherPromptLabel`,
    defaultMessage: 'Label text',
  },
  addOtherAlias: {
    id: `${scope}.addOtherAlias`,
    defaultMessage: 'OTH',
  },
  isRequiredLabel: {
    id: `${scope}.isRequiredLabel`,
    defaultMessage: 'Required',
  },
  questionPlaceholder: {
    // TODO: remove duplicates from Screening component etc.
    id: `${scope}.questionPlaceholder`,
    defaultMessage: 'Type your question here...',
  },
  answerPlaceholder: {
    id: `${scope}.answerPlaceholder`,
    defaultMessage: 'Type next answer',
  },
  descriptionPlaceholder: {
    id: `${scope}.descriptionPlaceholder`,
    defaultMessage: 'Type your text here',
  },
  answerFirstPlaceholder: {
    id: `${scope}.answerFirstPlaceholder`,
    defaultMessage: 'Type an answer',
  },
  choicePlaceholder: {
    id: `${scope}.choicePlaceholder`,
    defaultMessage: 'Enter a choice',
  },
  sliderLabel: {
    id: `${scope}.sliderLabel`,
    defaultMessage: 'Set slide range',
  },
  fromLabel: {
    id: `${scope}.fromLabel`,
    defaultMessage: 'From',
  },
  toLabel: {
    id: `${scope}.toLabel`,
    defaultMessage: 'To',
  },
  stepLabel: {
    id: `${scope}.stepLabel`,
    defaultMessage: 'Step',
  },
  showLabels: {
    id: `${scope}.showLabels`,
    defaultMessage: 'Show values',
  },
  npsPlaceholder: {
    id: `${scope}.npsPlaceholder`,
    defaultMessage:
      'How likely is it that you would recommend [ company ] to a friend or colleague?',
  },
  recallInformation: {
    id: `${scope}.recallInformation`,
    defaultMessage: 'Recall information',
  },
  recallQuestion: {
    id: `${scope}.recallQuestion`,
    defaultMessage: 'Recall question',
  },
  newLabel: {
    id: `${scope}.newLabel`,
    defaultMessage: 'New!',
  },
  carryForward: {
    featureTitle: {
      id: `${scope}.carryForward.featureTitle`,
      defaultMessage: 'Carry answers from',
    },
    questionPlaceholder: {
      id: `${scope}.carryForward.questionPlaceholder`,
      defaultMessage: 'Choose Question',
    },
    answerPlaceholder: {
      id: `${scope}.carryForward.answerPlaceholder`,
      defaultMessage: 'Choose type of answers',
    },
    compareAnswerSpecificRows: {
      id: `${scope}.carryForward.compareAnswerSpecificRows`,
      defaultMessage: 'Choose answers',
    },
    compareAnswerMatrixRows: {
      id: `${scope}.carryForward.compareAnswerMatrixRows`,
      defaultMessage: 'Choose statements',
    },
    compareAnswerMatrixColumns: {
      id: `${scope}.carryForward.compareAnswerMatrixColumns`,
      defaultMessage: 'Choose scale points',
    },
    compareAnswerRanking: {
      id: `${scope}.carryForward.compareAnswerRanking`,
      defaultMessage: 'Choose value',
    },
    compareAnswerBipolar: {
      id: `${scope}.carryForward.compareAnswerBipolar`,
      defaultMessage: 'Choose scale points',
    },
    disabledTooltip: {
      id: `${scope}.carryForward.disabledTooltip`,
      defaultMessage:
        "This setting is disabled because this question's answers are already being carried forward.",
    },
    disabledTooltipFirstQuestion: {
      id: `${scope}.carryForward.disabledTooltipFirstQuestion`,
      defaultMessage:
        "This setting is disabled because there isn't any prior answer to carry forward.",
    },
    disabledOther: {
      id: `${scope}.carryForward.disabledOther`,
      defaultMessage:
        '"Other" option cannot be enabled when this question\'s answers are being carried forward.',
    },
    disabledTooltipConstantSumMinMax: {
      id: `${scope}.carryForward.disabledTooltipConstantSumMinMax`,
      defaultMessage:
        'This setting is disabled because other enabled settings block it.',
    },
    disabledTooltipQuotas: {
      id: `${scope}.carryForward.disabledTooltipQuotas`,
      defaultMessage:
        'This setting is disabled because answers quotas are enabled.',
    },
    disabledTooltipMinimumResponses: {
      id: `${scope}.carryForward.disabledTooltipMinimumResponses`,
      defaultMessage:
        'This setting is disabled because minimum responses are enabled.',
    },
    addQuestion: {
      id: `${scope}.carryForward.addQuestion`,
      defaultMessage: '+ Add question',
    },
    fromQuestions: {
      id: `${scope}.carryForward.fromQuestions`,
      defaultMessage: 'Questions',
    },
    fromAudience: {
      id: `${scope}.carryForward.fromAudience`,
      defaultMessage: 'Audience',
    },
    // //////
    [ANSWER_OPTIONS.SELECTED_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SELECTED_VALUES}`,
      defaultMessage: 'Selected answers',
    },
    [ANSWER_OPTIONS.UNSELECTED_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.UNSELECTED_VALUES}`,
      defaultMessage: 'Unselected answers',
    },
    [ANSWER_OPTIONS.ALL_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.ALL_VALUES}`,
      defaultMessage: 'All answers',
    },
    [ANSWER_OPTIONS.VALUE]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.VALUE}`,
      defaultMessage: 'Value',
    },
    [ANSWER_OPTIONS.GREATER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.GREATER_THAN}`,
      defaultMessage: 'Greater than',
    },
    [ANSWER_OPTIONS.LESSER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.LESSER_THAN}`,
      defaultMessage: 'Less than',
    },
    [ANSWER_OPTIONS.EQUAL_TO]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.EQUAL_TO}`,
      defaultMessage: 'Equal to',
    },
    [ANSWER_OPTIONS.HIGHEST_VALUE]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.HIGHEST_VALUE}`,
      defaultMessage: 'Highest value',
    },
    [ANSWER_OPTIONS.LOWEST_VALUE]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.LOWEST_VALUE}`,
      defaultMessage: 'Lowest value',
    },
    [ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES}`,
      defaultMessage: 'Specific selected answers',
    },
    [ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES}`,
      defaultMessage: 'Specific unselected answers',
    },

    [ANSWER_OPTIONS.ALL_ROWS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.ALL_ROWS}`,
      defaultMessage: 'All statements',
    },
    [ANSWER_OPTIONS.ALL_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.ALL_COLUMNS}`,
      defaultMessage: 'All scale points',
    },
    [ANSWER_OPTIONS.ALL_ROWS_ALL_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.ALL_ROWS_ALL_COLUMNS}`,
      defaultMessage: 'All scale points and all statements',
    },
    [ANSWER_OPTIONS.SELECTED_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SELECTED_COLUMNS}`,
      defaultMessage: 'Selected scale points',
    },
    [ANSWER_OPTIONS.UNSELECTED_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.UNSELECTED_COLUMNS}`,
      defaultMessage: 'Unselected scale points',
    },
    [ANSWER_OPTIONS.SELECTED_FOR_ROW]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SELECTED_FOR_ROW}`,
      defaultMessage: 'Selected scale points of specific statements',
    },
    [ANSWER_OPTIONS.UNSELECTED_FOR_ROW]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.UNSELECTED_FOR_ROW}`,
      defaultMessage: 'Unselected scale points of specific statements',
    },
    [ANSWER_OPTIONS.SELECTED_FOR_COLUMN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SELECTED_FOR_COLUMN}`,
      defaultMessage: 'Statements for selected scale points',
    },
    [ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN}`,
      defaultMessage: 'Statements for unselected scale points',
    },
    [ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS}`,
      defaultMessage: 'Specific selected scale points',
    },
    [ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS}`,
      defaultMessage: 'Specific unselected scale points',
    },
    // bipolar CF options
    [ANSWER_OPTIONS.MATRIX_SELECTED_FOR_ROW]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_SELECTED_FOR_ROW}`,
      defaultMessage: 'Selected scale points for specific statements',
    },
    [ANSWER_OPTIONS.MATRIX_EQUAL]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_EQUAL}`,
      defaultMessage: 'Statements for specific scale points',
    },
    [ANSWER_OPTIONS.MATRIX_NOT_EQUAL]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_NOT_EQUAL}`,
      defaultMessage: 'Statements with a scale point NOT equal to',
    },
    [ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE}`,
      defaultMessage: 'Statements with the highest scale point',
    },
    [ANSWER_OPTIONS.MATRIX_LOWEST_VALUE]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_LOWEST_VALUE}`,
      defaultMessage: 'Statements with the lowest scale point',
    },
    [ANSWER_OPTIONS.MATRIX_GREATER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_GREATER_THAN}`,
      defaultMessage: 'Statements with a scale point greater than',
    },
    [ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN}`,
      defaultMessage: 'Statements with a scale point equal / greater than',
    },
    [ANSWER_OPTIONS.MATRIX_LESSER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_LESSER_THAN}`,
      defaultMessage: 'Statements with a scale point less than',
    },
    [ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN}`,
      defaultMessage: 'Statement with a scale point equal / less than',
    },
    [ANSWER_OPTIONS.MATRIX_ALL_ROWS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.MATRIX_ALL_ROWS}`,
      defaultMessage: 'All statements',
    },
    [ANSWER_OPTIONS.VISIBLE_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.VISIBLE_VALUES}`,
      defaultMessage: 'Displayed answers',
    },
    [ANSWER_OPTIONS.HIDDEN_VALUES]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.HIDDEN_VALUES}`,
      defaultMessage: 'Not displayed answers',
    },
    [ANSWER_OPTIONS.VISIBLE_ROWS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.VISIBLE_ROWS}`,
      defaultMessage: 'Displayed statements',
    },
    [ANSWER_OPTIONS.HIDDEN_ROWS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.HIDDEN_ROWS}`,
      defaultMessage: 'Not displayed statements',
    },
    [ANSWER_OPTIONS.VISIBLE_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.VISIBLE_COLUMNS}`,
      defaultMessage: 'Displayed scale points',
    },
    [ANSWER_OPTIONS.HIDDEN_COLUMNS]: {
      id: `${scope}.carryForward.${ANSWER_OPTIONS.HIDDEN_COLUMNS}`,
      defaultMessage: 'Not displayed scale points',
    },
  },
  redundantWords: {
    age: {
      id: `${scope}.redundantWords.age`,
      defaultMessage:
        "Heads-up! If your question is about a user's age, we already ask this so you can filter your results later",
    },
    demographics: {
      id: `${scope}.redundantWords.demographics`,
      defaultMessage:
        "Heads up! If your question is about a user's demographic data, we provide this for free when you get your results.",
    },
    gender: {
      id: `${scope}.redundantWords.gender`,
      defaultMessage:
        "Heads-up! If your question is about a user's gender, we already ask this so you can filter your results later",
    },
    pii: {
      id: `${scope}.redundantWords.pii`,
      defaultMessage:
        "Heads up! If your question is about a user's personal data like name, e-mail, address, etc. keep in mind that your survey will be rejected in the review process.",
    },
    other: {
      id: `${scope}.redundantWords.other`,
      defaultMessage:
        'Heads up! We suggest you add "Other" or "None of the Above" by enabling them on the questions settings',
    },
  },
  errors: {
    questionTooLong: {
      id: `${scope}.errors.questionTooLong`,
      defaultMessage:
        "Yikes! That's a really long question. The limit is {maxLength} characters",
    },
    answerTooLong: {
      id: `${scope}.errors.answerTooLong`,
      defaultMessage:
        "Yikes! That's a really long answer. The limit is {maxLength} characters",
    },
    answerTooLongTable: {
      id: `${scope}.errors.answerTooLongTable`,
      defaultMessage: 'The limit is {maxLength} characters',
    },
    quotaSum: {
      id: `${scope}.errors.quotaSum`,
      defaultMessage: 'The sum needs to add up to {completes}',
    },
    minimumQuotaSumExceedsCompletes: {
      id: `${scope}.errors.minimumQuotaSumExceedsCompletes`,
      defaultMessage: 'The sum of minimum responses cannot exceed {completes}',
    },
    missingPrompt: {
      id: `${scope}.errors.missingPrompt`,
      defaultMessage: 'Whoops! Please enter a question prompt to continue',
    },
    missingDescriptionPrompt: {
      id: `${scope}.errors.missingDescriptionPrompt`,
      defaultMessage: 'Whoops! Please enter a description text to continue',
    },
    missingAnswers: {
      id: `${scope}.errors.missingAnswers`,
      defaultMessage: 'Please add at least one answer to the question',
    },
    missingAnswersWithCF: {
      id: `${scope}.errors.missingAnswersWithCF`,
      defaultMessage:
        'Please add at least one answer to this question. Maybe display logic has been applied on source question. If the sender question type is multiple selection, you might need to use the "Limit answers" setting.',
    },
    missingScorepointsWithCF: {
      id: `${scope}.errors.missingScorepointsWithCF`,
      defaultMessage:
        'Please add at least one scale point to this question. If the sender question type is multiple selection, you might need to use the "Limit answers" setting.',
    },
    missingTicks: {
      id: `${scope}.errors.missingTicks`,
      defaultMessage:
        'Help us out! Please fill out all labels in order to continue',
    },
    emptyAnswers: {
      id: `${scope}.emptyAnswers`,
      defaultMessage: 'Please fill out all answers',
    },
    emptyOtherAnswer: {
      id: `${scope}.emptyOtherAnswer`,
      defaultMessage: 'Please fill out the "Other" answer and label',
    },
    emptyNoneOfTheAboveAnswer: {
      id: `${scope}.emptyNoneOfTheAboveAnswer`,
      defaultMessage: 'Please fill out the "None of the above" answer',
    },
    limitMaxSmaller: {
      id: `${scope}.errors.limitMaxSmaller`,
      defaultMessage:
        'Your min value must be lower than or equal to your max value',
    },
    limitOutOfBounds: {
      id: `${scope}.errors.limitOutOfBounds`,
      defaultMessage:
        'Min/max values must be less than or equal to the number of available answers (non-exclusive)',
    },
    limitMissingValues: {
      id: `${scope}.errors.limitMissingValues`,
      defaultMessage: 'Please add a min and a max limit',
    },
    limitPriceRangesMaxSmaller: {
      id: `${scope}.errors.limitPriceRangesMaxSmaller`,
      defaultMessage: 'Your min value must be lower than your max value',
    },
    limitPriceRangesOutOfBounds: {
      id: `${scope}.errors.limitPriceRangesOutOfBounds`,
      defaultMessage:
        'The limits can range from 1 to the number of answers you added',
    },
    limitPriceRangesMissingValues: {
      id: `${scope}.errors.limitPriceRangesMissingValues`,
      defaultMessage: 'Please add a min and a max limit',
    },
    rankingMissingValues: {
      id: `${scope}.errors.rankingMissingValues`,
      defaultMessage: 'Labels cannot be empty',
    },
    decimalLimitOutOfBounds: {
      id: `${scope}.errors.decimalLimitOutOfBounds`,
      defaultMessage: 'Maximum allowed decimals can range from 0 to 10',
    },
    decimalLimitMissingValues: {
      id: `${scope}.errors.decimalLimitMissingValues`,
      defaultMessage: 'Please add a maximum decimal value',
    },
    minMaxRangeInvalid: {
      id: `${scope}.errors.minMaxRangeInvalid`,
      defaultMessage: 'Please provide a min and a max value',
    },
    minGreaterThanMax: {
      id: `${scope}.errors.minGreaterThanMax`,
      defaultMessage: 'Woah! Your min value must be lower than your max value',
    },
    minRangeTooSmall: {
      id: `${scope}.errors.minRangeTooSmall`,
      defaultMessage: 'Min must be at least 0',
    },
    missingAllowedAnswer: {
      id: `${scope}.errors.missingAllowedAnswer`,
      defaultMessage: 'Question needs to have at least one allowed answer',
    },
    missingNotAllowedAnswer: {
      id: `${scope}.errors.missingNotAllowedAnswer`,
      defaultMessage: 'Question needs to have at least one not allowed answer',
    },
    sliderStepsNotInt: {
      id: `${scope}.errors.sliderStepsNotInt`,
      defaultMessage: '[(To - From) / Step] should be an integer',
    },
    sliderFromLarger: {
      id: `${scope}.errors.sliderFromLarger`,
      defaultMessage:
        'Woah! Your "from" value must be lower than your "to" value',
    },
    sliderStepTooSmall: {
      id: `${scope}.errors.sliderStepTooSmall`,
      defaultMessage:
        'Please make sure you have fewer unique values than 1000 ((To - From) / Step).',
    },
    sliderMissingValues: {
      id: `${scope}.errors.sliderMissingValues`,
      defaultMessage:
        'You must enter from, to and step values in order to continue',
    },
    sliderTooManyAnswers: {
      id: `${scope}.errors.sliderTooManyAnswers`,
      defaultMessage:
        'Answers cannot be more than {threshold} (Answer count must be smaller than (To-From) / step + 1)',
    },
    matrixMissingRows: {
      id: `${scope}.errors.matrixMissingRows`,
      defaultMessage:
        'Help us out! Please fill out all statements in order to continue',
    },
    matrixMissingColumns: {
      id: `${scope}.errors.matrixMissingColumns`,
      defaultMessage:
        'Help us out! Please fill out all scale points in order to continue',
    },
    emptyScalepoints: {
      id: `${scope}.errors.emptyScalepoints`,
      defaultMessage:
        'Help us out! Please fill out all scale points to continue',
    },
    emptyScalepointLabels: {
      id: `${scope}.errors.emptyScalepointLabels`,
      defaultMessage: 'Help us out! Please fill out all labels to continue',
    },
    tooManyAnswers: {
      id: `${scope}.errors.tooManyAnswers`,
      defaultMessage:
        'You have reached the limit of total answers in this question',
    },
    tooManyAnswersWithCF: {
      id: `${scope}.errors.tooManyAnswersWithCF`,
      defaultMessage:
        'You might exceed the limit of total answers in this question (because of the carried forward answers)',
    },
    tooManyAnswersWithCFRanking: {
      id: `${scope}.errors.tooManyAnswersWithCFRanking`,
      defaultMessage:
        'Please verify that the provided number of possible answers does not exceed 10 which is the maximum limit for "Ranking" question type.',
    },
    tooManyScorepointsWithCF: {
      id: `${scope}.errors.tooManyScorepointsWithCF`,
      defaultMessage:
        'You might exceed the limit of total scale points in this question (because of the carried forward answers)',
    },
    onlyAnswerSelectionWithPossibleZeroCarryForward: {
      id: `${scope}.errors.onlyAnswerSelectionWithPossibleZeroCarryForward`,
      defaultMessage:
        'Please provide at least one answer to always ensure an available option, even if carried-forward options are empty.',
    },
    onlyAnswerSelectionWithPossibleZeroCarryForwardRows: {
      id: `${scope}.errors.onlyAnswerSelectionWithPossibleZeroCarryForwardRows`,
      defaultMessage:
        'Please provide at least one statement to always ensure an available option, even if carried-forward options are empty.',
    },
    onlyAnswerSelectionWithPossibleZeroCarryForwardColumns: {
      id: `${scope}.errors.onlyAnswerSelectionWithPossibleZeroCarryForwardColumns`,
      defaultMessage:
        'Please provide at least one scale point to always ensure an available option, even if carried-forward options are empty.',
    },
    incompleteCarryForwardConfiguration: {
      id: `${scope}.errors.incompleteCarryForwardConfiguration`,
      defaultMessage: 'Please complete the carry forward configuration',
    },
    batchRowsExceeded: {
      id: `${scope}.errors.batchRowsExceeded`,
      defaultMessage:
        'You have exceeded the number of statements you can add (expected: {expectedRows})',
    },
    batchColumnsExceeded: {
      id: `${scope}.errors.batchColumnsExceeded`,
      defaultMessage:
        'You have exceeded the number of scale points you can add (expected: {expectedColumns})',
    },
    decimalsInvalid: {
      id: `${scope}.errors.decimalsInvalid`,
      defaultMessage: 'Decimal is invalid',
    },
    decimalsZero: {
      id: `${scope}.errors.decimalsZero`,
      defaultMessage:
        'Decimal count cannot be zero. If you want no decimals please disable the setting',
    },
    delimiterError: {
      id: `${scope}.errors.delimiterError`,
      defaultMessage: 'Please use the | delimiter at most once per line.',
    },
    constantSumTotalError: {
      id: `${scope}.errors.constantSumTotalError`,
      defaultMessage: 'Sum total must be greater than zero.',
    },
    minMaxGreaterThanSumTotal: {
      id: `${scope}.errors.minMaxGreaterThanSumTotal`,
      defaultMessage: 'Limit ranges cannot be greater than the total sum',
    },
    minMaxRangeInvalidSumTotal: {
      id: `${scope}.errors.minMaxRangeInvalidSumTotal`,
      defaultMessage: 'Limit ranges misconfiguration with total sum',
    },
    totalTooManyTotalDecimals: {
      id: `${scope}.errors.totalTooManyTotalDecimals`,
      defaultMessage:
        'Total sum decimals must be less than or equal to the "Allow decimal numbers" setting',
    },
    whitespacesForStatements: {
      id: `${scope}.errors.whitespacesForStatements`,
      defaultMessage: 'Please fill the statement. Alternatively leave it empty',
    },
    repetitionsBiggerThanConceptsLength: {
      id: `${scope}.errors.repetitionsBiggerThanConceptsLength`,
      defaultMessage:
        'Enter a number that is less than or equal to the available concepts',
    },
    duplicateCarryForwardAnswer: {
      id: `${scope}.errors.duplicateCarryForwardAnswer`,
      defaultMessage:
        'Please check your carry-forward configuration. You cannot carry forward a question that is already part of another nested forward setup.',
    },
  },
  blockingErrors: {
    mandatoryComments: {
      label: {
        id: `${scope}.blockingErrors.mandatoryComments.label`,
        defaultMessage: 'Unresolved mandatory comments!',
      },
      description: {
        id: `${scope}.blockingErrors.mandatoryComments.description`,
        defaultMessage:
          'Please review and resolve all the mandatory question comments to proceed.',
      },
    },
    invalidLogic: {
      label: {
        id: `${scope}.blockingErrors.invalidLogic.label`,
        defaultMessage: 'Respondents will be trapped in a loop!',
      },
      description: {
        id: `${scope}.blockingErrors.invalidLogic.description`,
        defaultMessage:
          'There are questions pointing to previous questions creating a loop. Please update the questionnaire logic.',
      },
    },
    nonIncludedQuestions: {
      label: {
        id: `${scope}.blockingErrors.nonIncludedQuestions.label`,
        defaultMessage: 'Some questions are skipped!',
      },
      description: {
        id: `${scope}.blockingErrors.nonIncludedQuestions.description`,
        defaultMessage:
          'To resolve this you may update the logic of the questionnaire or remove them.',
      },
    },
    hiddenAnswerReference: {
      label: {
        id: `${scope}.blockingErrors.hiddenAnswerReference.label`,
        defaultMessage: 'Review logic',
      },
      description: {
        id: `${scope}.blockingErrors.hiddenAnswerReference.description`,
        defaultMessage:
          'Some rules still use an answer you removed. Edit or delete the rules highlighted in red, or restore the answer.',
      },
    },
    wrongReference: {
      label: {
        id: `${scope}.blockingErrors.wrongReference.label`,
        defaultMessage: 'Invalid logic!',
      },
      description: {
        id: `${scope}.blockingErrors.wrongReference.description`,
        defaultMessage:
          'A rule depends on a future question. To resolve this, you should update the logic of the questionnaire.',
      },
    },
  },
  confirmModal: {
    confirmText: {
      id: `${scope}.confirmModal.confirmText`,
      defaultMessage: 'Delete it',
    },
    cancelText: {
      id: `${scope}.confirmModal.cancelText`,
      defaultMessage: 'Keep it',
    },
    question: {
      title: {
        id: `${scope}.confirmModal.question.title`,
        defaultMessage: 'This question contains logic',
      },
      titleCarryForward: {
        id: `${scope}.confirmModal.question.titleCarryForward`,
        defaultMessage:
          'This question contains logic at a carried forward answer',
      },
      body: {
        id: `${scope}.confirmModal.question.body`,
        defaultMessage:
          'Removing this question will invalidate any logic rules that depend on it and may affect all subsequent answers governed by those rules. <b>Are you sure you want to remove the entire question, including all logic rules it contains and all rules that reference its answers?</b>',
      },
    },
    questionCFRemove: {
      title: {
        id: `${scope}.confirmModal.questionCFRemove.title`,
        defaultMessage: 'This question has been carried forward',
      },
      body: {
        id: `${scope}.confirmModal.questionCFRemove.body`,
        defaultMessage:
          'If you delete this question, all answers that have been carried forward will also be removed. <strong>Are you sure you want to delete it?</strong>',
      },
    },
    questionLoopAndMergeRemove: {
      title: {
        id: `${scope}.confirmModal.questionLoopAndMergeRemove.title`,
        defaultMessage:
          'This question contains answers that are being used in loop and merge settings',
      },
      body: {
        id: `${scope}.confirmModal.questionLoopAndMergeRemove.body`,
        defaultMessage:
          'If you delete this question, these loop and merge settings will be removed. <strong>Are you sure you want to delete it?</strong>',
      },
    },
    answer: {
      title: {
        id: `${scope}.confirmModal.answer.title`,
        defaultMessage: 'This answer contains logic',
      },
      titleCarryForward: {
        id: `${scope}.confirmModal.answer.titleCarryForward`,
        defaultMessage:
          'This answer contains logic at a carry forward question',
      },
      body: {
        id: `${scope}.confirmModal.answer.body`,
        defaultMessage:
          'Removing this answer option will invalidate any logic rules that depend on it and may affect all subsequent answers governed by those rules. <b>Are you sure you want to remove it along with all related logic rules?</b>',
      },
    },
    statement: {
      title: {
        id: `${scope}.confirmModal.statement.title`,
        defaultMessage: 'This statement contains logic',
      },
      titleCarryForward: {
        id: `${scope}.confirmModal.statement.titleCarryForward`,
        defaultMessage:
          'This statement contains logic at a carry forward question',
      },
      body: {
        id: `${scope}.confirmModal.statement.body`,
        defaultMessage:
          'Removing this statement will invalidate any logic rules that depend on it and may affect all subsequent answers governed by those rules. <b>Are you sure you want to remove it along with all related logic rules?</b>',
      },
    },
    scalepoint: {
      title: {
        id: `${scope}.confirmModal.scalepoint.title`,
        defaultMessage: 'This scale point contains logic',
      },
      titleCarryForward: {
        id: `${scope}.confirmModal.scalepoint.titleCarryForward`,
        defaultMessage:
          'This scale point contains logic at a carry forward question',
      },
      body: {
        id: `${scope}.confirmModal.scalepoint.body`,
        defaultMessage:
          'Removing this scale point will invalidate any logic rules that depend on it and may affect all subsequent answers governed by those rules. <b>Are you sure you want to remove it along with all related logic rules?</b>',
      },
    },
    node: {
      title: {
        id: `${scope}.confirmModal.node.title`,
        defaultMessage: 'Remove {entityName}?',
      },
      bodyBefore: {
        id: `${scope}.confirmModal.node.bodyBefore`,
        defaultMessage:
          'The {entityName} that you are trying to remove is currently have or use in other question or questions the following features:',
      },
      bodyAfter: {
        id: `${scope}.confirmModal.node.bodyAfter`,
        defaultMessage:
          "Removing it will remove these features from this {entityName} and will alter the questions' features that are using this {entityName}'s content as a reference. <b>Are you sure you want to continue?</b>",
      },
      bodyGroupNode: {
        id: `${scope}.confirmModal.node.bodyGroupNode`,
        defaultMessage:
          'Removing the {entityName} will also remove all of its questions. <b>Are you sure you want to continue?</b>',
      },
      bodyGroupNodeWithFeatures: {
        id: `${scope}.confirmModal.node.bodyGroupNodeWithFeatures`,
        defaultMessage:
          'Removing the {entityName} will also remove all of its questions. The {entityName} or some of its questions have or use some of the following features:',
      },
    },
    recallAnswer: {
      title: {
        id: `${scope}.confirmModal.recallAnswer.title`,
        defaultMessage: 'This answer is being recalled',
      },
      body: {
        id: `${scope}.confirmModal.recallAnswer.body`,
        defaultMessage:
          'This answer is being recalled from other questions. If you remove this answer, the recall will be removed from those questions. <b>Are you sure you want to continue?</b>',
      },
    },
    recallInformation: {
      title: {
        id: `${scope}.confirmModal.recallInformation.title`,
        defaultMessage: 'This question recalls information',
      },
      body: {
        id: `${scope}.confirmModal.recallInformation.body`,
        defaultMessage:
          'This question recalls information from other question or questions. If you move/remove the question, it will not recall this information anymore. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal.recallInformation.confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal.recallInformation.cancelText`,
        defaultMessage: 'Cancel',
      },
    },
    moveNode: {
      title: {
        id: `${scope}.confirmModal.moveNode.title`,
        defaultMessage: 'This question will lose some information!',
      },
      titlePlural: {
        id: `${scope}.confirmModal.moveNode.titlePlural`,
        defaultMessage: 'The questions {labels} will lose some information!',
      },
      titleUnderEditWithCarryForward: {
        id: `${scope}.confirmModal.moveNode.titleUnderEditWithCarryForward`,
        defaultMessage: 'This question cannot be moved in a group!',
      },
      titlePluralUnderEditWithCarryForward: {
        id: `${scope}.confirmModal.moveNode.titlePluralUnderEditWithCarryForward`,
        defaultMessage: 'This questions cannot be moved in a group!',
      },
      body: {
        id: `${scope}.confirmModal.moveNode.body`,
        defaultMessage:
          'The following settings are enabled for this or a subsequent question:<br/> <b>{settingsEnabled}</b> <br/>If you move the question, the information from those settings will be removed. <b>Are you sure you want to continue?</b>',
      },
      bodyPlural: {
        id: `${scope}.confirmModal.moveNode.bodyPlural`,
        defaultMessage:
          'The following settings are enabled for these questions:<br/> <b>{settingsEnabled}</b> <br/>If you move these questions, the information from those settings will be removed. <b>Are you sure you want to continue?</b>',
      },
      bodyUnderEditWithCarryForward: {
        id: `${scope}.confirmModal.moveNode.bodyUnderEditWithCarryForward`,
        defaultMessage:
          'The following settings are enabled for this question:<br/> <b>{settingsEnabled}</b>',
      },
      bodyPluralUnderEditWithCarryForward: {
        id: `${scope}.confirmModal.moveNode.bodyPluralUnderEditWithCarryForward`,
        defaultMessage:
          'The following settings are enabled for these questions:<br/> <b>{settingsEnabled}</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal.moveNode.confirmText`,
        defaultMessage: 'Continue',
      },
      confirmTextUnderEditWithCarryForward: {
        id: `${scope}.confirmModal.moveNode.confirmTextUnderEditWithCarryForward`,
        defaultMessage: 'OK',
      },
      cancelText: {
        id: `${scope}.confirmModal.moveNode.cancelText`,
        defaultMessage: 'Cancel',
      },
      settings: {
        hasRecall: {
          id: `${scope}.confirmModal.moveNode.settings.hasRecall`,
          defaultMessage: 'Recall information',
        },
        hasLogic: {
          id: `${scope}.confirmModal.moveNode.settings.hasLogic`,
          defaultMessage: 'Advanced Logic',
        },
        hasCarryForward: {
          id: `${scope}.confirmModal.moveNode.settings.hasCarryForward`,
          defaultMessage: 'Carried Forward Answers',
        },
        hasSourceCarryForward: {
          id: `${scope}.confirmModal.moveNode.settings.hasSourceCarryForward`,
          defaultMessage: 'Forwarding answers to another question',
        },
        hasQuotaLogic: {
          id: `${scope}.confirmModal.moveNode.settings.hasQuotaLogic`,
          defaultMessage: 'answers quotas',
        },
        hasConditionalQuotaLogic: {
          id: `${scope}.confirmModal.moveNode.settings.hasConditionalQuotaLogic`,
          defaultMessage: 'conditional quotas',
        },
      },
    },
    enableGroupShufflingConfirmationForLosses: {
      title: {
        id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.title`,
        defaultMessage: '{lossTypes} functionality will be lost!',
      },
      bodyEnableGroupShuffling: {
        id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.bodyEnableGroupShuffling`,
        defaultMessage:
          'If you enable shuffling, {lossTypes} functionality inside this group will be removed. <b>Are you sure you want to continue?</b>',
      },
      bodyEnableDisplaySamePage: {
        id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.bodyEnableDisplaySamePage`,
        defaultMessage:
          'If you enable display questions on same page, {lossTypes} functionality inside this group will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.cancelText`,
        defaultMessage: 'Cancel',
      },
      settings: {
        hasRecall: {
          id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.settings.hasRecall`,
          defaultMessage: 'recall information',
        },
        hasLogic: {
          id: `${scope}.confirmModal.enableGroupShufflingConfirmationForLosses.settings.hasLogic`,
          defaultMessage: 'logic',
        },
      },
    },
    enableShufflingConfirmationForLosses: {
      title: {
        id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.title`,
        defaultMessage: '{lossTypes} functionality will be lost!',
      },
      body: {
        id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.body`,
        defaultMessage:
          'If you enable shuffling, {lossTypes} functionality will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.cancelText`,
        defaultMessage: 'Cancel',
      },
      settings: {
        hasRecall: {
          id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.settings.hasRecall`,
          defaultMessage: 'recall information',
        },
        hasLogic: {
          id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.settings.hasLogic`,
          defaultMessage: 'logic',
        },
        hasAdvancedQuota: {
          id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.settings.hasAdvancedQuota`,
          defaultMessage: 'conditional quota',
        },
        hasCarryForward: {
          id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.settings.hasCarryForward`,
          defaultMessage: 'carry forward',
        },
        hasLoopAndMerge: {
          id: `${scope}.confirmModal.enableShufflingConfirmationForLosses.settings.hasSourceCarryForward`,
          defaultMessage: 'loop and merge from question',
        },
      },
    },
    [CONFIRM_MODAL_TYPES.CHANGE_QUESTION_THAT_CF]: {
      title: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_THAT_CF
        }].title`,
        defaultMessage: 'This question contains carry forward answers',
      },
      body: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_THAT_CF
        }].body`,
        defaultMessage:
          'By changing the type of this question, the carry forward setting will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_THAT_CF
        }].confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_THAT_CF
        }].cancelText`,
        defaultMessage: 'Cancel',
      },
    },
    [CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF]: {
      title: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF
        }].title`,
        defaultMessage: 'This question has answers being carried forward',
      },
      body: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF
        }].body`,
        defaultMessage:
          'By changing the type of this question, the carry forward setting will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF
        }].confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF
        }].cancelText`,
        defaultMessage: 'Cancel',
      },
    },
    [CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_LOOP_AND_MERGE]: {
      title: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_LOOP_AND_MERGE
        }].title`,
        defaultMessage:
          'This question contains answers that are being used in loop and merge settings',
      },
      body: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_LOOP_AND_MERGE
        }].body`,
        defaultMessage:
          'By changing the type of this question, these loop and merge settings will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_LOOP_AND_MERGE
        }].confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_LOOP_AND_MERGE
        }].cancelText`,
        defaultMessage: 'Cancel',
      },
    },
    [CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF_AND_LOOP_AND_MERGE]: {
      title: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF_AND_LOOP_AND_MERGE
        }].title`,
        defaultMessage:
          'This question contains answers that are carried forward and used in loop and merge settings',
      },
      body: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF_AND_LOOP_AND_MERGE
        }].body`,
        defaultMessage:
          'By changing the type of this question, these carry forward and loop and merge settings will be removed. <b>Are you sure you want to continue?</b>',
      },
      confirmText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF_AND_LOOP_AND_MERGE
        }].confirmText`,
        defaultMessage: 'Continue',
      },
      cancelText: {
        id: `${scope}.confirmModal[${
          CONFIRM_MODAL_TYPES.CHANGE_QUESTION_WITH_CF_AND_LOOP_AND_MERGE
        }].cancelText`,
        defaultMessage: 'Cancel',
      },
    },
  },
  moveModal: {
    back: {
      id: `${scope}.moveModal.back`,
      defaultMessage: 'Back',
    },
    next: {
      id: `${scope}.moveModal.next`,
      defaultMessage: 'Next',
    },
    cancel: {
      id: `${scope}.moveModal.cancel`,
      defaultMessage: 'Cancel',
    },
    submit: {
      id: `${scope}.moveModal.submit`,
      defaultMessage: 'Move',
    },
    newGroup: {
      id: `${scope}.moveModal.newGroup`,
      defaultMessage: 'Create New Group',
    },
    moveQuestionAfter: {
      id: `${scope}.moveModal.moveQuestionAfter`,
      defaultMessage: 'Move Question {prefix}Q{index} After:',
    },
    moveGroupAfter: {
      id: `${scope}.moveModal.moveGroupAfter`,
      defaultMessage: 'Move Group {index} After:',
    },
    moveAbTestAfter: {
      id: `${scope}.moveModal.moveAbTestAfter`,
      defaultMessage: 'Move A/B Test After:',
    },
    moveConjointAfter: {
      id: `${scope}.moveModal.moveConjointAfter`,
      defaultMessage: 'Move Conjoint After:',
    },
    moveVanWestendorpAfter: {
      id: `${scope}.moveModal.moveConjointAfter`,
      defaultMessage: 'Move Van Westendorp After:',
    },
    moveMaxDiffAfter: {
      id: `${scope}.moveModal.moveMaxDiffAfter`,
      defaultMessage: 'Move Maxdiff After:',
    },
    moveQuestionTo: {
      id: `${scope}.moveModal.moveQuestionTo`,
      defaultMessage: 'Move Question Q{index} to:',
    },
    moveQuestionIntoGroup: {
      id: `${scope}.moveModal.moveQuestionIntoGroup`,
      defaultMessage: 'Move questions to Group',
    },
    moveQuestionIntoAbTest: {
      id: `${scope}.moveModal.moveQuestionIntoAbTest`,
      defaultMessage: 'Move questions to A/B Test',
    },
    chooseGroup: {
      id: `${scope}.moveModal.chooseGroup`,
      defaultMessage: 'Choose Group',
    },
    chooseQuestion: {
      id: `${scope}.moveModal.chooseQuestion`,
      defaultMessage: 'Choose Question',
    },
    andAfterQuestionTitle: {
      id: `${scope}.moveModal.andAfterQuestionTitle`,
      defaultMessage: '...and After Question:',
    },
  },
  warnings: {
    tooFewRows: {
      id: `${scope}.warnings.tooFewRows`,
      defaultMessage: 'Heads-up! Your question contains only one statement',
    },
    tooFewColumns: {
      id: `${scope}.warnings.tooFewColumns`,
      defaultMessage: 'Heads-up! Your question contains only one scale point',
    },
    tooFewAnswers: {
      id: `${scope}.warnings.tooFewAnswers`,
      defaultMessage: 'Heads-up! Your question contains only one answer',
    },
    allAnswersAreExclusive: {
      id: `${scope}.warnings.allAnswersAreExclusive`,
      defaultMessage:
        'Heads-up! You have marked all answers as exclusive, consider using a single selection type of question.',
    },
    allScorepointsAreExclusive: {
      id: `${scope}.warnings.allScorepointsAreExclusive`,
      defaultMessage:
        'Heads-up! You have marked all scale points as exclusive, consider using a single selection - matrix table type of question.',
    },
    distributionLinkNumberOfCompletes: {
      id: `${scope}.warnings.distributionQuotas`,
      defaultMessage: `Heads up! You haven't set a 'Number of completes' yet. When the answers quota is fulfilled, your survey will not receive further responses.`,
    },
    allFieldsNonRequired: {
      id: `${scope}.warnings.allFieldsNonRequired`,
      defaultMessage:
        'Heads-up! When answering the questionnaire, the respondent will be required to fill in at least one field.',
    },
  },
  randomizeQuestions: {
    id: `${scope}.randomizeQuestions`,
    defaultMessage: 'Randomize Questionnaire',
  },
  randomizeQuestionsEnabled: {
    id: `${scope}.randomizeQuestionsEnabled`,
    defaultMessage:
      "Advanced logic can't be used when randomize questionnaire option is enabled",
  },
  randomizeQuestionsEnabledAnswersLogic: {
    id: `${scope}.randomizeQuestionsEnabled`,
    defaultMessage:
      "Answers logic can't be used when randomize questionnaire option is enabled",
  },
  activeQuestionQuotas: {
    id: `${scope}.activeQuestionQuotas`,
    defaultMessage:
      "Answer display logic can't be used when question quotas are enabled.",
  },
  randomizeQuestionsEnabledHiddenVariables: {
    id: `${scope}.randomizeQuestionsEnabledHiddenVariables`,
    defaultMessage:
      "Hidden variables can't be used when randomize questionnaire option is enabled",
  },
  randomizeQuestionsEnabledAdvancedQuota: {
    id: `${scope}.randomizeQuestionsEnabledAdvancedQuota`,
    defaultMessage:
      "Conditional quota can't be used when randomize questionnaire option is enabled",
  },
  randomizeQuestionsTooltip: {
    id: `${scope}.randomizeQuestionsTooltip`,
    defaultMessage:
      '<b>Randomize Questionnaire</b> lets you change the order of the questions in your survey.<br/><br/><b>Reverse options</b> flips the order of items, with a 50% chance.<br/><br/><b>Keep first/last fixed</b> protects those positions while the rest shuffle.',
  },
  shuffleQuestionsTitle: {
    id: `${scope}.shuffleQuestionsTitle`,
    defaultMessage: 'Shuffle questions',
  },
  randomizeSubQuestions: {
    id: `${scope}.randomizeSubQuestions`,
    defaultMessage: 'Randomize sub-questions',
  },
  randomizeSubQuestionsTooltip: {
    id: `${scope}.randomizeSubQuestionsTooltip`,
    defaultMessage:
      '<b>Randomize sub-questions</b> lets you change the order of the questions in this group.<br/><br/><b>Reverse options</b> flips the order of items, with a 50% chance.<br/><br/><b>Keep first/last fixed</b> protects those positions while the rest shuffle.',
  },
  status: {
    id: `${scope}.status`,
    defaultMessage: 'Status',
  },
  discardChanges: {
    id: `${scope}.discardChanges`,
    defaultMessage: 'Discard changes',
  },
  discardPrompt: {
    id: `${scope}.discardPrompt`,
    defaultMessage:
      "If you go back, you will lose the changes you haven't saved.",
  },
  discardConfirm: {
    id: `${scope}.discardConfirm`,
    defaultMessage: 'Discard and go back',
  },
  saveChanges: {
    id: `${scope}.saveChanges`,
    defaultMessage: 'Save Changes',
  },
  exclusiveButtonTooltip: {
    id: `${scope}.exclusiveButtonTooltip`,
    defaultMessage: 'Mark answer as exclusive',
  },
  exclusiveButtonTooltipDeselect: {
    id: `${scope}.exclusiveButtonTooltipDeselect`,
    defaultMessage: 'Unmark answer as exclusive',
  },
  addLimitsTextLength: {
    id: `${scope}.addLimitsTextLength`,
    defaultMessage: 'Limit character length',
  },
  addLimitsTextLengthMax: {
    id: `${scope}.addLimitsTextLengthMax`,
    defaultMessage: 'Max',
  },
  addLimitsTextLengthMin: {
    id: `${scope}.addLimitsTextLengthMin`,
    defaultMessage: 'Min',
  },
  addLimitsTextLengthTooltip: {
    id: `${scope}.addLimitsTextLengthTooltip`,
    defaultMessage:
      'Set a specific character length requirement for your responses with minimum and/or maximum options. Please note that the sentence "This is a new response!" contains 23 characters (spaces and punctuation included).',
  },
  enabledQuotas: {
    id: `${scope}.enabledQuotas`,
    defaultMessage: 'Add quota',
  },
  enabledMinimumResponses: {
    id: `${scope}.enabledMinimumResponses`,
    defaultMessage: 'Minimum responses',
  },
  answerQuotaModeLabel: {
    id: `${scope}.answerQuotaModeLabel`,
    defaultMessage: 'Answer quotas',
  },
  enabledMinimumResponsesTooltip: {
    id: `${scope}.enabledMinimumResponsesTooltip`,
    defaultMessage:
      'The minimum responses feature is restricted to the first <b>eight questions</b> of the survey path. This limit prevents late-stage disqualifications and optimizes the respondent experience. Minimum responses guarantee a baseline number of respondents for specific answers, ensuring that you collect enough data for the less popular choices. Enter 0 for answers that don’t require a minimum limit.',
  },
  enabledQuotasTooltipPeeq: {
    id: `${scope}.enabledQuotasTooltipPeeq`,
    defaultMessage:
      'The answer quota feature is restricted to the first <b>eight questions</b> of the survey path. This limit prevents late-stage disqualifications and optimizes the respondent experience.',
  },
  cannotEnableMinimumResponsesWithAnswerLimits: {
    id: `${scope}.cannotEnableMinimumResponsesWithAnswerLimits`,
    defaultMessage:
      "Minimum responses aren't supported when answer limits are enabled.",
  },
  cannotEnableAnswerLimitsWithMinimumResponses: {
    id: `${scope}.cannotEnableAnswerLimitsWithMinimumResponses`,
    defaultMessage:
      "Answer limits aren't supported when minimum responses are enabled.",
  },
  cannotEnableMinimumResponsesWithQuotas: {
    id: `${scope}.cannotEnableMinimumResponsesWithQuotas`,
    defaultMessage:
      'Quota and minimum responses are mutually exclusive and cannot be active at the same time.',
  },
  cannotEnableQuotasWithMinimumResponses: {
    id: `${scope}.cannotEnableQuotasWithMinimumResponses`,
    defaultMessage:
      'Quota and minimum responses are mutually exclusive and cannot be active at the same time.',
  },
  quotaModeChangeErrorTitle: {
    id: `${scope}.quotaModeChangeErrorTitle`,
    defaultMessage: "Couldn't update the quota setting",
  },
  quotaModeChangeErrorDescription: {
    id: `${scope}.quotaModeChangeErrorDescription`,
    defaultMessage:
      'Something went wrong while changing this question’s quota setting. Please try again.',
  },
  advancedQuota: {
    id: `${scope}.advancedQuota`,
    defaultMessage: 'Add conditional quota',
  },
  versionHistory: {
    id: `${scope}.versionHistory`,
    defaultMessage: 'Version history',
  },
  previewQuestions: {
    id: `${scope}.previewQuestions`,
    defaultMessage: 'Preview questions',
  },
  screening: {
    id: `${scope}.screening`,
    defaultMessage: 'Screening',
  },
  askAI: {
    id: `${scope}.askAI`,
    defaultMessage: 'Ask AI',
  },
  hiddenVariablesDisabled: {
    id: `${scope}.hiddenVariablesDisabled`,
    defaultMessage:
      "Hidden variables can't be used when randomize questionnaire option is enabled",
  },
  aiBuilder: {
    id: `${scope}.aiBuilder`,
    defaultMessage: 'AI Builder',
  },
  tabTooltip: {
    questionnaire: {
      id: `${scope}.tabTooltip.questionnaire`,
      defaultMessage: 'Edit questionnaire',
    },
    advancedLogic: {
      id: `${scope}.tabTooltip.advancedLogic`,
      defaultMessage: 'Edit advanced logic',
    },
    hiddenVariables: {
      id: `${scope}.tabTooltip.hiddenVariables`,
      defaultMessage: 'Edit hidden variables',
    },
    disqualificationLogic: {
      id: `${scope}.tabTooltip.disqualificationLogic`,
      defaultMessage: 'Edit disqualification logic',
    },
    answersLogic: {
      id: `${scope}.tabTooltip.answersLogic`,
      defaultMessage: 'Edit display logic',
    },
    quotaLogic: {
      id: `${scope}.tabTooltip.quotaLogic`,
      defaultMessage: 'Conditional quota',
    },
    surveyTheme: {
      id: `${scope}.tabTooltip.surveyTheme`,
      defaultMessage: 'Edit survey theme',
    },
  },
  batchAnswersWithMentions: {
    id: `${scope}.batchAnswersWithMentions`,
    defaultMessage: 'Batching answers is disabled due to Recall functionality.',
  },
  displaySamePageSetting: {
    id: `${scope}.displaySamePageSetting`,
    defaultMessage: 'Single page view',
  },
  displaySamePageSettingTooltip: {
    id: `${scope}.displaySamePageSettingTooltip`,
    defaultMessage:
      'Display grouped questions on the same page instead of separate screens.',
  },
  loopAndMergeSetting: {
    id: `${scope}.loopAndMergeSetting`,
    defaultMessage: 'Loop & Merge',
  },
  loopAndMergeTooltip: {
    id: `${scope}.loopAndMergeTooltip`,
    defaultMessage:
      'Create a dynamic set of questions by replacing specific pieces of information in questions or answer prompts and repeating them for responses.',
  },
  loopAndMergeShuffle: {
    id: `${scope}.loopAndMergeShuffle`,
    defaultMessage: 'Randomize loop order',
  },
  loopAndMergeRepetitions: {
    id: `${scope}.loopAndMergeRepetitions`,
    defaultMessage: 'Max number of loops',
  },
  loopAndMergeBasedOnQuestion: {
    id: `${scope}.loopAndMergeBasedOnQuestion`,
    defaultMessage: 'Based off of a question',
  },
  cannotDeleteCarriedForwardAnswerTooltip: {
    id: `${scope}.cannotDeleteCarriedForwardAnswerTooltip`,
    defaultMessage: 'To remove this answer you need to disable carry forward',
  },
  [ActionVisibilityValueType.Answer]: {
    id: `${scope}.${ActionVisibilityValueType.Answer}`,
    defaultMessage: 'answer',
  },
  [ActionVisibilityValueType.Row]: {
    id: `${scope}.${ActionVisibilityValueType.Row}`,
    defaultMessage: 'statement',
  },
  [ActionVisibilityValueType.Column]: {
    id: `${scope}.${ActionVisibilityValueType.Column}`,
    defaultMessage: 'scale point',
  },
  answerSettings: {
    id: `${scope}.answerSettings`,
    defaultMessage: 'Answer settings',
  },
  questionSettings: {
    id: `${scope}.questionSettings`,
    defaultMessage: 'Question settings',
  },
  methodSettings: {
    id: `${scope}.methodSettings`,
    defaultMessage: 'Method settings',
  },
  statementSettings: {
    id: `${scope}.statementSettings`,
    defaultMessage: 'Statement settings',
  },
  scalePointSettings: {
    id: `${scope}.scalePointSettings`,
    defaultMessage: 'Scale point settings',
  },
  attributeSettings: {
    id: `${scope}.attributeSettings`,
    defaultMessage: 'Attribute settings',
  },
  loopSettings: {
    id: `${scope}.loopSettings`,
    defaultMessage: 'Loop settings',
  },
  variableSettings: {
    id: `${scope}.variableSettings`,
    defaultMessage: 'Variable settings',
  },
  quotasCount: {
    id: `${scope}.quotasCount`,
    defaultMessage: 'Count',
  },
  quotasProgress: {
    id: `${scope}.quotasProgress`,
    defaultMessage: 'Quota progress',
  },
  quotasAssigned: {
    id: `${scope}.quotasAssigned`,
    defaultMessage: 'Assigned',
  },
  quotasTarget: {
    id: `${scope}.quotasTarget`,
    defaultMessage: 'Target',
  },
  quotasRemaining: {
    id: `${scope}.quotasRemaining`,
    defaultMessage: 'Remaining',
  },
  minimumResponsesCount: {
    id: `${scope}.minimumResponsesCount`,
    defaultMessage: 'Count',
  },
  minimumResponsesProgress: {
    id: `${scope}.minimumResponsesProgress`,
    defaultMessage: 'Target progress',
  },
  minimumResponsesTarget: {
    id: `${scope}.minimumResponsesTarget`,
    defaultMessage: 'Minimum target',
  },
  minimumResponsesRemaining: {
    id: `${scope}.minimumResponsesRemaining`,
    defaultMessage: 'Remaining',
  },
  cannotEditQuotas: {
    id: `${scope}.cannotEditQuotas`,
    defaultMessage: `Quotas can't be edited under the current conditions.`,
  },
  cannotEnableMinimumResponses: {
    id: `${scope}.cannotEnableMinimumResponses`,
    defaultMessage: `Minimum responses can't be enabled under the current conditions.`,
  },
  cannotEnableQuotasWithVisibilityAnswers: {
    id: `${scope}.cannotEnableQuotasWithVisibilityAnswers`,
    defaultMessage: `Quotas aren't supported on questions with logic rules.`,
  },
  cannotEnableMinimumResponsesWithVisibilityAnswers: {
    id: `${scope}.cannotEnableMinimumResponsesWithVisibilityAnswers`,
    defaultMessage:
      "Minimum responses aren't supported on questions with logic rules.",
  },
  cannotEnableQuotasWithCarryForward: {
    id: `${scope}.cannotEnableQuotasWithVisibilityAnswers`,
    defaultMessage: `Quotas aren't supported with carried-forward answers. You can still use a conditional quota.`,
  },
  cannotEnableMinimumResponsesWithCarryForward: {
    id: `${scope}.cannotEnableMinimumResponsesWithCarryForward`,
    defaultMessage:
      "Minimum responses aren't supported with carried-forward answers.",
  },
  cannotEnableQuotasWithoutEnoughAnswers: {
    id: `${scope}.cannotEnableQuotasWithoutEnoughAnswers`,
    defaultMessage: `Quotas need more than one visible answer or scalepoint on this question.`,
  },
  cannotEnableMinimumResponsesWithoutEnoughAnswers: {
    id: `${scope}.cannotEnableMinimumResponsesWithoutEnoughAnswers`,
    defaultMessage:
      'Minimum responses need more than one visible answer or scalepoint on this question.',
  },
  carryForwardAndQuotas: {
    title: {
      id: `${scope}.carryForwardAndQuotas.title`,
      defaultMessage: 'Quotas with carried forward answers',
    },
    body: {
      id: `${scope}.carryForwardAndQuotas.body`,
      defaultMessage:
        'Heads up! You cannot have quotas when you have carried forward answers in your questionnaire. If you proceed, the quotas will be removed. Are you sure you want to continue?',
    },
    confirm: {
      id: `${scope}.carryForwardAndQuotas.confirm`,
      defaultMessage: 'Okay',
    },
  },
  hasDisplayLogicAndQuotas: {
    title: {
      id: `${scope}.hasDisplayLogic.title`,
      defaultMessage: 'Quotas with answer display logic',
    },
    body: {
      id: `${scope}.hasDisplayLogic.body`,
      defaultMessage:
        'Heads up! You cannot have quotas when you have answer display logic in your questionnaire. If you proceed, the quotas will be removed. Are you sure you want to continue?',
    },
    confirm: {
      id: `${scope}.hasDisplayLogic.confirm`,
      defaultMessage: 'Okay',
    },
  },
  loopAndMergeWithQuotas: {
    title: {
      id: `${scope}.loopAndMergeWithQuotas.title`,
      defaultMessage: 'Quotas and logic with Loop and Merge',
    },
    body: {
      id: `${scope}.loopAndMergeWithQuotas.body`,
      defaultMessage:
        'Heads up! Loop and Merge groups cannot have quotas on inner questions or group logic that references them. If you proceed, the affected quotas and rules will be removed. Are you sure you want to continue?',
    },
    confirm: {
      id: `${scope}.loopAndMergeWithQuotas.confirm`,
      defaultMessage: 'Okay',
    },
  },
  quotaAnswersSetting: {
    multipleAssignment: {
      id: `${scope}.quotaAnswersSetting.multipleAssignment`,
      defaultMessage: 'Multiple assignment',
    },
    singleAssignment: {
      id: `${scope}.quotaAnswersSetting.singleAssignment`,
      defaultMessage: 'Single assignment',
    },
    multiple: {
      id: `${scope}.quotaAnswersSetting.multiple`,
      defaultMessage:
        'Each response counts toward all selected answers, <strong>which may fill multiple quotas at once</strong>. This can lead to increased disqualifications if any of those quotas reach their limit, or pause the survey once all quotas are fulfilled.',
    },
    single: {
      id: `${scope}.quotaAnswersSetting.single`,
      defaultMessage:
        'Each response counts toward only one selected answer, <strong>chosen based on how quotas are set to fill</strong>. This helps minimize disqualifications by routing respondents to quotas that are still open.',
    },
    leastFillCount: {
      id: `${scope}.quotaAnswersSetting.leastFillCount`,
      defaultMessage:
        'Responses will be assigned to the <strong>{order}</strong> quota based on <strong>{base}</strong>.',
    },
    fillOrder: {
      id: `${scope}.quotaAnswersSetting.fillOrder`,
      defaultMessage: 'Fill order',
    },
    basedOn: {
      id: `${scope}.quotaAnswersSetting.basedOn`,
      defaultMessage: 'Based on',
    },
    assignedTooltip: {
      id: `${scope}.quotaAnswersSetting.assignedTooltip`,
      defaultMessage:
        'Responses are assigned on <b>{fillOrder}</b> based on <b>{basedOn}</b>',
    },
    [QUOTA_FILL_ORDER.LEAST_FILLED]: {
      id: `${scope}.quotaAnswersSetting.leastFilledValue`,
      defaultMessage: 'Least filled',
    },
    [QUOTA_FILL_ORDER.MOST_FILLED]: {
      id: `${scope}.quotaAnswersSetting.mostFilledValue`,
      defaultMessage: 'Most filled',
    },
    [QUOTA_BASED_ON.COUNT]: {
      id: `${scope}.quotaAnswersSetting.countValue`,
      defaultMessage: 'Count',
    },
    [QUOTA_BASED_ON.PERCENTAGE]: {
      id: `${scope}.quotaAnswersSetting.percentageValue`,
      defaultMessage: 'Percentage',
    },
    confirmReset: {
      title: {
        id: `${scope}.quotaAnswersSetting.confirmReset.title`,
        defaultMessage: 'Switch to multiple assignment?',
      },
      body: {
        id: `${scope}.quotaAnswersSetting.confirmReset.body`,
        defaultMessage:
          'Switching to multiple assignment will clear the assigned counts.',
      },
      confirm: {
        id: `${scope}.quotaAnswersSetting.confirmReset.confirm`,
        defaultMessage: 'Switch to multiple assignment',
      },
      cancel: {
        id: `${scope}.quotaAnswersSetting.confirmReset.cancel`,
        defaultMessage: 'Cancel',
      },
    },
  },
  questionAnswerDropdown: {
    moveDown: {
      id: `${scope}.questionAnswerDropdown.moveDown`,
      defaultMessage: 'Move down',
    },
    moveUp: {
      id: `${scope}.questionAnswerDropdown.moveUp`,
      defaultMessage: 'Move up',
    },
    moveLeft: {
      id: `${scope}.questionAnswerDropdown.moveLeft`,
      defaultMessage: 'Move left',
    },
    moveRight: {
      id: `${scope}.questionAnswerDropdown.moveRight`,
      defaultMessage: 'Move right',
    },
    markAsExclusive: {
      id: `${scope}.questionAnswerDropdown.markAsExclusive`,
      defaultMessage: 'Mark as exclusive',
    },
    unmarkAsExclusive: {
      id: `${scope}.questionAnswerDropdown.unmarkAsExclusive`,
      defaultMessage: 'Unmark as exclusive',
    },
    applyDisplayLogic: {
      id: `${scope}.questionAnswerDropdown.applyDisplayLogic`,
      defaultMessage: 'Apply display logic',
    },
    editDisplayLogic: {
      id: `${scope}.questionAnswerDropdown.editDisplayLogic`,
      defaultMessage: 'Edit display logic',
    },
    editAdvancedLogic: {
      id: `${scope}.questionAnswerDropdown.editAdvancedLogic`,
      defaultMessage: 'Edit advanced logic',
    },
    addMedia: {
      id: `${scope}.questionAnswerDropdown.addMedia`,
      defaultMessage: 'Add media',
    },
    editMedia: {
      id: `${scope}.questionAnswerDropdown.editMedia`,
      defaultMessage: 'Edit media',
    },
    removeQuestion: {
      id: `${scope}.questionAnswerDropdown.removeQuestion`,
      defaultMessage: 'Remove question',
    },
    addToFavorites: {
      id: `${scope}.questionAnswerDropdown.addToFavorites`,
      defaultMessage: 'Add to favorites',
    },
    removeFromFavorites: {
      id: `${scope}.questionAnswerDropdown.removeFromFavorites`,
      defaultMessage: 'Remove from favorites',
    },
    removeNode: {
      id: `${scope}.questionAnswerDropdown.removeNode`,
      defaultMessage: 'Remove',
    },
    removeGroup: {
      id: `${scope}.questionAnswerDropdown.removeGroup`,
      defaultMessage: 'Remove group',
    },
    removeMethod: {
      id: `${scope}.questionAnswerDropdown.removeMethod`,
      defaultMessage: 'Remove method',
    },
    removeAttribute: {
      id: `${scope}.questionAnswerDropdown.removeAttribute`,
      defaultMessage: 'Remove attribute',
    },
    removeAnswer: {
      id: `${scope}.questionAnswerDropdown.removeAnswer`,
      defaultMessage: 'Remove answer',
    },
    aiActions: {
      id: `${scope}.questionAnswerDropdown.aiActions`,
      defaultMessage: 'AI actions',
    },
    generateDesign: {
      id: `${scope}.questionAnswerDropdown.generateDesign`,
      defaultMessage: 'Generate design',
    },
    updateDesign: {
      id: `${scope}.questionAnswerDropdown.updateDesign`,
      defaultMessage: 'Update design',
    },
    creatingDesign: {
      id: `${scope}.questionAnswerDropdown.creatingDesign`,
      defaultMessage: 'Creating design...',
    },
    designCreated: {
      id: `${scope}.questionAnswerDropdown.designCreated`,
      defaultMessage: 'Design created',
    },
    downloadDesign: {
      id: `${scope}.questionAnswerDropdown.downloadDesign`,
      defaultMessage: 'Download design',
    },
    generateDesignFail: {
      id: `${scope}.questionAnswerDropdown.generateDesignFail`,
      defaultMessage: 'Design generation failed',
    },
    generateDesignFailDesc: {
      id: `${scope}.questionAnswerDropdown.generateDesignFailDesc`,
      defaultMessage:
        'Something went wrong while creating the design. Please try again.',
    },
    downloadDesignFail: {
      id: `${scope}.questionAnswerDropdown.downloadDesignFail`,
      defaultMessage: 'Design download failed',
    },
    downloadDesignFailDesc: {
      id: `${scope}.questionAnswerDropdown.downloadDesignFailDesc`,
      defaultMessage:
        'Something went wrong while downloading the design. Please try again.',
    },
  },
});

export default messages;

export const QUOTA_MODE_LABEL = {
  [QUOTA_MODE.MAX]: messages.answerQuotaModeLabel,
  [QUOTA_MODE.MIN]: messages.enabledMinimumResponses,
};
